/*
 * config-cart-sync.js
 * --------------------------------------------------------------------------
 * Keeps a configured product "bundle" in sync in the cart drawer and on the
 * cart page. A bundle is one base line (`_is_base`) plus N component lines
 * (`_is_component`) that share a `_config_id` line item property and are
 * grouped by snippets/cart-config-group.liquid into a single
 * `<li class="cart-config-group" data-config-id data-cf-group-keys="k1,k2,...">`.
 *
 *   - Removing the base line removes the whole group.
 *   - Changing the base line quantity sets every line in the group to that qty
 *     (qty <= 0 removes the group). Add-on lines have no controls of their own.
 *
 * Why this lives in a persistent asset (it used to be an inline <script> inside
 * cart-config-group.liquid): the drawer re-renders by swapping innerHTML, and
 * <script> tags injected via innerHTML never execute - so that inline listener
 * only ever attached when the page was server-rendered with a non-empty cart.
 * Loaded once here (cart-drawer.liquid -> rendered globally by theme.liquid) its
 * document-level listeners survive every re-render. They run in capture phase so
 * they intercept the theme's native single-line remove / quantity handlers
 * (cart.js) before those fire, turning a single-line action into a group action.
 */
(function () {
  if (window.__configCartSyncInit) return;
  window.__configCartSyncInit = true;

  var GROUP = ".cart-config-group";
  var REMOVE_SELECTOR =
    "cart-remove-button, .wt-cart__item__remove, .wt-cart__item__remove--title, .wt-cart__item__remove--amount";
  var QTY_DEBOUNCE_MS = 300;

  var inFlight = {}; // config_id -> true while a /cart/update.js request is open
  var qtyTimers = {}; // config_id -> debounce timer

  // Re-render whichever cart UI is on the page. cart-drawer:refresh updates the
  // drawer + cart icon bubble; PUB_SUB cartUpdate re-renders cart-page line items
  // (CartItems.onCartUpdate). Both are harmless no-ops where their target is absent.
  function refresh() {
    document.dispatchEvent(
      new CustomEvent("cart-drawer:refresh", { bubbles: true, composed: true })
    );
    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "config-cart-sync" });
    }
  }

  // Set every line in the group to `qty` in a single atomic request.
  function setGroupQuantity(group, qty) {
    var id = group.getAttribute("data-config-id") || "";
    if (inFlight[id]) return;

    var keys = (group.getAttribute("data-cf-group-keys") || "")
      .split(",")
      .filter(Boolean);
    if (!keys.length) return;

    var updates = {};
    keys.forEach(function (key) {
      updates[key] = qty;
    });

    inFlight[id] = true;
    group.classList.add("cart-config-group--updating");

    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error("cart update failed (" + r.status + ")");
        refresh();
      })
      .catch(function (e) {
        group.classList.remove("cart-config-group--updating");
        // eslint-disable-next-line no-console
        console.error("[config-cart-sync]", e);
        window.alert(
          "Sorry, we could not update your configured item. Please try again."
        );
      })
      .finally(function () {
        delete inFlight[id];
      });
  }

  // --- Remove cascade -------------------------------------------------------
  // Any remove control inside a config group removes the whole group.
  document.addEventListener(
    "click",
    function (e) {
      var hit = e.target.closest(REMOVE_SELECTOR);
      if (!hit) return;
      var group = hit.closest(GROUP);
      if (!group) return; // standalone item - let the theme handle it normally
      e.preventDefault();
      e.stopImmediatePropagation();
      setGroupQuantity(group, 0);
    },
    true
  );

  // --- Quantity sync --------------------------------------------------------
  // The base line's <quantity-counter> dispatches a bubbling `change` on its
  // .js-counter-quantity input (for both +/- clicks and manual typing). We catch
  // it in capture phase, stop it reaching cart.js (single-line update), and push
  // the new quantity to every line in the group instead.
  document.addEventListener(
    "change",
    function (e) {
      var input = e.target;
      if (
        !input ||
        !input.classList ||
        !input.classList.contains("js-counter-quantity")
      ) {
        return;
      }
      var base = input.closest(".cart-config-group__base");
      if (!base) return; // not a configured bundle's base line
      var group = base.closest(GROUP);
      if (!group) return;

      e.stopImmediatePropagation();

      var qty = parseInt(input.value, 10);
      if (isNaN(qty)) return; // cleared field - ignore; refresh restores server state

      var id = group.getAttribute("data-config-id") || "cfg";
      clearTimeout(qtyTimers[id]);
      qtyTimers[id] = setTimeout(function () {
        setGroupQuantity(group, qty < 0 ? 0 : qty);
      }, QTY_DEBOUNCE_MS);
    },
    true
  );
})();
